import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { cloudInfoAPI } from '../services/api';
import { getApiBaseUrl, getMainRegion, getSmVersion, getUiVersion } from '../config/runtimeConfig';
import NodeGroupManagement from './NodeGroupManagement';
import Logo from './Logo';
import { LogOut, ChevronDown, Cloud, MapPin, Layers, Server, Disc, Map, Copy, Check, Key, Globe, Info, X, Search } from 'lucide-react';

const Dashboard: React.FC = () => {
  const [showStreamManagerInfo, setShowStreamManagerInfo] = useState(true);

  const [cloudInfoLoading, setCloudInfoLoading] = useState(true);
  const [cloudInfoError, setCloudInfoError] = useState('');
  const [regions, setRegions] = useState<any>({});
  const [images, setImages] = useState<any>({});
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const [regionsSectionExpanded, setRegionsSectionExpanded] = useState<Record<string, boolean>>({});
  const [instanceTypesExpanded, setInstanceTypesExpanded] = useState<Record<string, boolean>>({});
  const [imagesSectionExpanded, setImagesSectionExpanded] = useState<Record<string, boolean>>({});
  const [instanceTypes, setInstanceTypes] = useState<any>({});
  const [cloudProviderDetailsOpen, setCloudProviderDetailsOpen] = useState(false);
  const [imageSearchByProvider, setImageSearchByProvider] = useState<Record<string, string>>({});
  const cloudProviderDetailsBackdropMousedownRef = useRef(false);

  // Try to get Stream Manager URL from env or sessionStorage
  const streamManagerUrl = getApiBaseUrl();
  
  const navigate = useNavigate();
  const location = useLocation();
  const openNodeGroupStatus = (location.state as { openNodeGroupStatus?: string } | null)?.openNodeGroupStatus;
  const logout = useAuthStore((state) => state.logout);
  const token = useAuthStore((state) => state.token);
  const smVersion = getSmVersion();
  const uiVersion = getUiVersion();
  const mainRegion = getMainRegion();
  const [jwtCopied, setJwtCopied] = useState(false);

  const handleCopyJwt = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setJwtCopied(true);
      setTimeout(() => setJwtCopied(false), 2000);
    } catch {
      // Copy failed silently
    }
  };

  useEffect(() => {
    setCloudInfoLoading(true);
    setCloudInfoError('');
    Promise.allSettled([
      cloudInfoAPI.getAvailableRegions(),
      cloudInfoAPI.getAvailableImages(),
      cloudInfoAPI.getAvailableInstanceTypes(),
    ]).then(([regionsResult, imagesResult, instanceTypesResult]) => {
      if (regionsResult.status === 'fulfilled') setRegions(regionsResult.value ?? {});
      if (imagesResult.status === 'fulfilled') setImages(imagesResult.value ?? {});
      if (instanceTypesResult.status === 'fulfilled') setInstanceTypes(instanceTypesResult.value ?? {});
      const firstError = [regionsResult, imagesResult, instanceTypesResult].find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
      if (firstError) {
        const err = firstError.reason;
        setCloudInfoError(err?.response?.data?.message || err?.message || 'Failed to fetch cloud info');
      }
      setCloudInfoLoading(false);
    });
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNodeGroupViewChange = useCallback((view: 'list' | 'status') => {
    setShowStreamManagerInfo(view === 'list');
  }, []);

  return (
    <div className="min-h-screen bg-red5-dark">
      {/* Header */}
      <header className="relative bg-red5-gradient border-b border-red5-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center justify-start flex-1 min-h-0">
              <Logo height="40px" width="100px" className="translate-y" />
            </div>
            <div className="flex items-center justify-center flex-1 min-h-0">
              <h1 className="text-2xl font-normal text-white leading-none">Stream Manager Admin UI</h1>
            </div>
            <div className="flex items-center justify-end flex-1 min-h-0 pr-24">
              <button
                onClick={handleLogout}
                className="text-white hover:text-red5-red flex items-center space-x-2 transition-colors duration-300"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
        {uiVersion && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 ml-1 text-red5-red text-sm font-normal leading-none shrink-0">
            Version: {uiVersion}
          </span>
        )}
      </header>

      {/* Main Content - full width when on Node Group Details */}
      <main className={`px-4 sm:px-6 lg:px-8 py-8 ${showStreamManagerInfo ? 'max-w-7xl mx-auto' : 'w-full max-w-none min-h-[calc(100vh-4rem)]'}`}>
        {/* Stream Manager Info – only on main dashboard, hidden on Node Group Details */}
        {showStreamManagerInfo && (
        <div className="mb-8">
          <div className="relative bg-red5-gradient border border-red5-border rounded-xl p-8 overflow-hidden">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-2xl font-normal text-white">Stream Manager Info</div>
            </div>
            <div className="pt-4">
              {cloudInfoLoading ? (
                <div className="text-red5-red flex items-center space-x-2"><Layers className="h-5 w-5 animate-spin" /><span>Loading cloud info...</span></div>
              ) : cloudInfoError ? (
                <div className="text-red5-red">{cloudInfoError}</div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Line 1: URL, Version */}
                    <div className="flex items-center space-x-3">
                      <Cloud className="h-5 w-5 text-red5-red shrink-0" />
                      <span className="font-medium text-white shrink-0">URL:</span>
                      <a
                        href={streamManagerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-red5-red break-all hover:text-white hover:underline transition-colors duration-300"
                      >
                        {streamManagerUrl}
                      </a>
                    </div>
                    {smVersion ? (
                      <div className="flex items-center space-x-3">
                        <Info className="h-4 w-4 text-red5-red shrink-0" />
                        <span className="font-medium text-white shrink-0">Version:</span>
                        <span className="font-mono text-red5-red break-all">{smVersion}</span>
                      </div>
                    ) : (
                      <div />
                    )}

                    {/* Line 2: Cloud Provider, Region */}
                    {Object.keys(regions).length > 0 ? (
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center space-x-3 min-w-0">
                          <MapPin className="h-5 w-5 text-red5-red shrink-0" />
                          <span className="font-medium text-white shrink-0">Cloud Provider:</span>
                          <span className="font-mono text-red5-red break-all">{Object.keys(regions).join(', ')}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCloudProviderDetailsOpen(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover transition-colors text-xs shrink-0"
                        >
                          <Info className="h-3.5 w-3.5" />
                          View details
                        </button>
                      </div>
                    ) : (
                      <div />
                    )}
                    {mainRegion ? (
                      <div className="flex items-center space-x-3">
                        <Globe className="h-5 w-5 text-red5-red shrink-0" />
                        <span className="font-medium text-white">Region:</span>
                        <span className="font-mono text-red5-red break-all">{mainRegion}</span>
                      </div>
                    ) : (
                      <div />
                    )}

                    {/* Line 3: JWT Token */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Key className="h-5 w-5 text-red5-red shrink-0" />
                      <span className="font-medium text-white shrink-0">JWT Token:</span>
                      <span className="font-mono text-red5-red break-all min-w-0">***</span>
                      {token && (
                        <button
                          type="button"
                          onClick={handleCopyJwt}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] border text-xs shrink-0 transition-colors ${jwtCopied ? 'border-red5-green bg-red5-green/20 text-red5-green' : 'border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover'}`}
                          title="Copy JWT Token"
                        >
                          {jwtCopied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                        </button>
                      )}
                    </div>
                    <div />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Cloud Provider Details Modal */}
        {cloudProviderDetailsOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onMouseDown={(e) => { cloudProviderDetailsBackdropMousedownRef.current = e.target === e.currentTarget; }}
            onClick={(e) => {
              if (cloudProviderDetailsBackdropMousedownRef.current && e.target === e.currentTarget) setCloudProviderDetailsOpen(false);
              cloudProviderDetailsBackdropMousedownRef.current = false;
            }}
            role="presentation"
          >
            <div
              className="bg-red5-gradient border border-red5-border rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-red5-border shrink-0">
                <h3 className="text-lg font-normal text-white flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-red5-red" />
                  Details for Cloud Provider
                </h3>
                <button
                  type="button"
                  onClick={() => setCloudProviderDetailsOpen(false)}
                  className="p-2 text-white hover:bg-red5-card-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-red5-red"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1 custom-scrollbar space-y-6">
                {cloudInfoLoading ? (
                  <div className="text-red5-red flex items-center space-x-2">
                    <Layers className="h-5 w-5 animate-spin" />
                    <span>Loading cloud info...</span>
                  </div>
                ) : cloudInfoError ? (
                  <div className="text-red5-red">{cloudInfoError}</div>
                ) : (
                  <div className="space-y-2">
                    {/* Regions */}
                    {Object.entries(regions).map(([provider, regionList]: any) => {
                      const list = regionList || [];
                      const regionsExpanded = regionsSectionExpanded[provider] ?? false;
                      return (
                        <div key={provider} className="rounded-lg border border-red5-border overflow-hidden">
                          <button
                            type="button"
                            aria-expanded={regionsExpanded}
                            className="w-full flex items-center justify-between px-3 py-2.5 bg-red5-card-dark hover:bg-red5-card-darker text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-red5-red focus:ring-inset transition-colors duration-200"
                            onClick={() => setRegionsSectionExpanded((prev) => ({ ...prev, [provider]: !prev[provider] }))}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Map className="h-4 w-4 text-red5-green shrink-0" />
                              <span className="font-normal">Regions</span>
                              <span className="text-xs opacity-80 font-mono">({list.length})</span>
                              {Object.keys(regions).length > 1 && (
                                <span className="text-white opacity-80 font-mono text-sm">{provider}</span>
                              )}
                            </div>
                            <ChevronDown className={`h-4 w-4 shrink-0 text-white transition-transform duration-200 ${regionsExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          {regionsExpanded && (
                            <div className="border-t border-red5-border px-3 py-2 flex flex-wrap gap-2 bg-red5-card-darker/50">
                              {list.length === 0 ? (
                                <span className="text-sm text-white opacity-70">No regions loaded</span>
                              ) : (
                                list.map((region: string) => (
                                  <span key={region} className="text-white text-xs font-mono">
                                    {region}
                                  </span>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Instance types and Images per provider */}
                    {(() => {
                      const providersWithResources = new Set<string>([
                        ...Object.keys(regions || {}),
                        ...Object.keys(images || {}),
                        ...Object.keys(instanceTypes || {}),
                      ]);
                      const multipleProviders = providersWithResources.size > 1;
                      return Array.from(providersWithResources).map((provider) => {
                        const typesList = instanceTypes && instanceTypes[provider];
                        const imagesData = images && images[provider];
                        const hasTypes = Array.isArray(typesList) && typesList.length > 0;
                        const hasImages = imagesData != null && (Array.isArray(imagesData) ? imagesData.length > 0 : typeof imagesData === 'object' && Object.keys(imagesData).length > 0);
                        if (!hasTypes && !hasImages) return null;
                        const typesExpanded = instanceTypesExpanded[provider] ?? false;
                        const imagesExpanded = imagesSectionExpanded[provider] ?? false;
                        const isRegionKeyed = imagesData && !Array.isArray(imagesData) && typeof imagesData === 'object';
                        return (
                          <div key={provider} className="space-y-2">
                            {multipleProviders && (
                              <div className="font-normal text-white font-mono text-sm opacity-90">{provider}</div>
                            )}
                            {hasTypes && (
                              <div className="rounded-lg border border-red5-border overflow-hidden">
                                <button
                                  type="button"
                                  aria-expanded={typesExpanded}
                                  className="w-full flex items-center justify-between px-3 py-2.5 bg-red5-card-dark hover:bg-red5-card-darker text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-red5-red focus:ring-inset transition-colors duration-200"
                                  onClick={() => setInstanceTypesExpanded((prev) => ({ ...prev, [provider]: !prev[provider] }))}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Server className="h-4 w-4 text-blue-500 shrink-0" />
                                    <span className="font-normal">Instance types</span>
                                    <span className="text-xs opacity-80 font-mono">({typesList.length})</span>
                                  </div>
                                  <ChevronDown className={`h-4 w-4 shrink-0 text-white transition-transform duration-200 ${typesExpanded ? 'rotate-180' : ''}`} />
                                </button>
                                {typesExpanded && (
                                  <div className="border-t border-red5-border px-3 py-2 flex flex-wrap gap-2 bg-red5-card-darker/50">
                                    {typesList.map((type: string) => (
                                      <span key={type} className="text-white text-xs font-mono truncate" title={type}>
                                        {type}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {hasImages && (
                              <div className="rounded-lg border border-red5-border overflow-hidden">
                                {(() => {
                                  const imagesCount = isRegionKeyed
                                    ? Object.values(imagesData).reduce((acc: number, arr: any) => acc + (Array.isArray(arr) ? arr.length : 0), 0)
                                    : (Array.isArray(imagesData) ? imagesData : []).length;
                                  return (
                                    <>
                                      <button
                                        type="button"
                                        aria-expanded={imagesExpanded}
                                        className="w-full flex items-center justify-between px-3 py-2.5 bg-red5-card-dark hover:bg-red5-card-darker text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-red5-red focus:ring-inset transition-colors duration-200"
                                        onClick={() => setImagesSectionExpanded((prev) => ({ ...prev, [provider]: !prev[provider] }))}
                                      >
                                        <div className="flex items-center gap-2 min-w-0">
                                          <Disc className="h-4 w-4 text-red5-gold shrink-0" />
                                          <span className="font-normal">Images</span>
                                          <span className="text-xs opacity-80 font-mono">({imagesCount})</span>
                                        </div>
                                        <ChevronDown className={`h-4 w-4 shrink-0 text-white transition-transform duration-200 ${imagesExpanded ? 'rotate-180' : ''}`} />
                                      </button>
                                      {imagesExpanded && (
                                        <div className="border-t border-red5-border px-3 py-2 space-y-2 bg-red5-card-darker/50">
                                          <div className="flex items-center gap-2">
                                            <Search className="h-4 w-4 text-white opacity-70 shrink-0" />
                                            <input
                                              type="text"
                                              placeholder="Filter images..."
                                              value={imageSearchByProvider[provider] ?? ''}
                                              onChange={(e) => setImageSearchByProvider((prev) => ({ ...prev, [provider]: e.target.value }))}
                                              className="flex-1 min-w-0 px-2 py-1.5 text-sm font-mono text-white bg-red5-card-dark border border-red5-border rounded focus:outline-none focus:ring-1 focus:ring-red5-red placeholder-white placeholder-opacity-50"
                                            />
                                          </div>
                                          {isRegionKeyed ? (
                                            Object.entries(imagesData).map(([region, imageList]: any) => {
                                              const regionKey = `${provider}-${region}`;
                                              const isRegionOpen = expandedRegion === regionKey;
                                              const list = Array.isArray(imageList) ? imageList : [];
                                              const query = (imageSearchByProvider[provider] ?? '').trim().toLowerCase();
                                              const filteredList = query ? list.filter((img: string) => img.toLowerCase().includes(query)) : list;
                                              return (
                                                <div key={region} className="rounded-lg border border-red5-border overflow-hidden border-l-2 border-l-red5-gold/50">
                                                  <button
                                                    type="button"
                                                    aria-expanded={isRegionOpen}
                                                    className="w-full flex items-center justify-between px-3 py-2 bg-red5-card-darker hover:bg-red5-card-dark text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-red5-red focus:ring-inset transition-colors duration-200"
                                                    onClick={() => setExpandedRegion(isRegionOpen ? null : regionKey)}
                                                  >
                                                    <div className={`flex items-center gap-2 min-w-0 ${query && filteredList.length > 0 ? 'text-red5-green' : ''}`}>
                                                      <span className="font-mono text-sm font-normal">{(region as string)}</span>
                                                      <span className="text-xs opacity-80 font-mono">({list.length}{query ? `, ${filteredList.length} match` : ''})</span>
                                                    </div>
                                                    <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isRegionOpen ? 'rotate-180' : ''}`} />
                                                  </button>
                                                  {isRegionOpen && (
                                                    <ul className="grid grid-cols-1 gap-1.5 list-none px-3 py-2 overflow-y-auto custom-scrollbar border-t border-red5-border max-h-72">
                                                      {filteredList.length === 0 ? (
                                                        <li className="text-xs text-white opacity-70 py-2">{query ? 'No images match filter' : 'No images'}</li>
                                                      ) : (
                                                        filteredList.map((img: string) => (
                                                          <li key={img} className="font-mono text-xs text-white break-all" title={img}>
                                                            {img}
                                                          </li>
                                                        ))
                                                      )}
                                                    </ul>
                                                  )}
                                                </div>
                                              );
                                            })
                                          ) : (
                                            (() => {
                                              const flatList = Array.isArray(imagesData) ? imagesData : [];
                                              const query = (imageSearchByProvider[provider] ?? '').trim().toLowerCase();
                                              const filteredList = query ? flatList.filter((img: string) => img.toLowerCase().includes(query)) : flatList;
                                              return (
                                                <ul className="grid grid-cols-1 gap-1.5 list-none overflow-y-auto custom-scrollbar max-h-72">
                                                  {filteredList.length === 0 ? (
                                                    <li className="text-xs text-white opacity-70 py-2">{query ? 'No images match filter' : 'No images'}</li>
                                                  ) : (
                                                    filteredList.map((img: string) => (
                                                      <li key={img} className="font-mono text-xs text-white break-all" title={img}>
                                                        {img}
                                                      </li>
                                                    ))
                                                  )}
                                                </ul>
                                              );
                                            })()
                                          )}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <NodeGroupManagement
          onViewChange={handleNodeGroupViewChange}
          initialSelectedGroupForStatus={openNodeGroupStatus || null}
        />
      </main>
    </div>
  );
};

export default Dashboard; 